import React, { useMemo } from "react";
import Draggable from "react-draggable";

import Portal from "../Portal";
import { useFloatingPanelLayout } from "./useFloatingPanelLayout";
import "./FloatingPanel.css";

export default function FloatingPanel({
    title = "",
    subtitle = "",
    onClose,
    initialPosition = null,
    defaultSize = { width: 480, height: 420 },
    minSize = { width: 360, height: 280 },
    maxSize = { width: 900, height: 720 },
    zIndex = 10050,
    overlay = false,
    centerInViewport = false,
    headerActions = null,
    footer = null,
    children,
    className = "",
    panelRef = null
}) {
    const titleId = useMemo(
        () => `floating-panel-title-${Math.random().toString(36).slice(2, 11)}`,
        []
    );
    const { panelRef: layoutPanelRef, isFlexCentered, shellStyle } = useFloatingPanelLayout({
        defaultSize,
        minSize,
        maxSize,
        initialPosition,
        centerInViewport
    });

    const setPanelRef = node => {
        layoutPanelRef.current = node;
        if (panelRef) panelRef.current = node;
    };

    const handleClose = e => {
        if (e) e.stopPropagation();
        if (typeof onClose === "function") onClose();
    };

    const panelNode = (
        <Draggable
            handle=".floating-panel__header"
            cancel=".floating-panel__close, .floating-panel__header-actions, .floating-panel__header-actions *"
        >
            <div
                ref={setPanelRef}
                className={`floating-panel${className ? ` ${className}` : ""}`}
                role="dialog"
                aria-labelledby={titleId}
            >
                <div className="floating-panel__header">
                    <div className="floating-panel__header-text">
                        <h5 id={titleId} className="floating-panel__title">
                            {title || "Panel"}
                        </h5>
                        {subtitle ? <p className="floating-panel__subtitle mb-0">{subtitle}</p> : null}
                    </div>
                    <div className="floating-panel__header-end">
                        {headerActions ? (
                            <div className="floating-panel__header-actions">{headerActions}</div>
                        ) : null}
                        <button
                            type="button"
                            className="floating-panel__close"
                            aria-label="Close panel"
                            onClick={handleClose}
                        >
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                </div>
                <div className="floating-panel__body">{children}</div>
                {footer ? <div className="floating-panel__footer">{footer}</div> : null}
            </div>
        </Draggable>
    );

    return (
        <Portal
            noOverlay={!overlay}
            zIndex={zIndex}
            onCancel={handleClose}
            body={
                isFlexCentered ? (
                    <div className="floating-panel-center-stage">
                        <div className="floating-panel-shell floating-panel-shell--centered">{panelNode}</div>
                    </div>
                ) : (
                    <div className="floating-panel-shell" style={shellStyle}>
                        {panelNode}
                    </div>
                )
            }
        />
    );
}
